import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/* ═══════════════════════════════════════════
   DILLY — Financial Due Diligence Portal v2
   Minimal · Consistent · Chat-first
═══════════════════════════════════════════ */

const T = {
  bg: "#F7F6F3",
  surface: "#FFFFFF",
  ink: "#1A2332",
  inkSoft: "#4A5568",
  accent: "#8B7355",
  accentLight: "#F0EBE3",
  accentMid: "#C4B49A",
  success: "#2D6A4F",
  warn: "#92700C",
  err: "#B33A2B",
  muted: "#8B8E96",
  mutedLight: "#C0C2C8",
  border: "#E4E2DC",
  white: "#FFFFFF",
};

const PRI_DOT = { Critical:T.err, High:T.warn, Medium:T.accent, Low:T.success };
const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'DM Sans', system-ui, sans-serif";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { margin:0; background:${T.bg}; }
  ::selection { background:${T.accentLight}; color:${T.ink}; }
  @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes slidePanel { from{transform:translateX(100%);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes pulse { 0%,100%{opacity:.35} 50%{opacity:.65} }
  @keyframes orbFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
  @keyframes orbGlow { 0%,100%{box-shadow:0 40px 80px rgba(20,110,120,.15),0 15px 35px rgba(0,0,0,.08),inset 0 -25px 40px rgba(0,0,0,.06)} 50%{box-shadow:0 40px 80px rgba(20,110,120,.2),0 15px 35px rgba(0,0,0,.1),inset 0 -25px 40px rgba(0,0,0,.06),0 0 60px rgba(60,200,200,.12)} }
  @keyframes orbSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes dotBlink { 0%,100%{opacity:.2} 50%{opacity:.7} }
  @keyframes bubbleFade { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  input:focus,textarea:focus{outline:none}
  @keyframes tipFadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spotPulse { 0%,100%{box-shadow:0 0 0 9999px rgba(26,35,50,.45)} 50%{box-shadow:0 0 0 9999px rgba(26,35,50,.5)} }
`;

/* ── Data ── */
const ITEMS = [
  /* ── 1. Business context ── */
  { id:"1.01", priority:"Medium", section:"Legal structure",
    request:"The current legal structure of the target group showing: all existing group entities, including dormant entities; entity type (e.g. partnership, trust) and location; any changes in the ownership structure during the FY22 to FY25 period; share ownership of the parent company.",
    guidance:"Typically found in the notes to your audited accounts. A corporate structure chart is ideal. Include any changes during FY22\u2013FY25 and the parent company share register.",
    example:"A PDF org chart showing: Holdco Ltd \u2192 OpCo Ltd (100%) \u2192 SubCo Ltd (100%), with entity type, jurisdiction and status for each.",
    fields:["Entity Name","Entity Type","Jurisdiction","Active/Dormant","Ownership %"],
    matchKeys:["structure","legal","entity","corporate","organigram","group","holdco"],
    scan:{ ok:true, summary:"Structure chart looks complete. All entities named with jurisdiction and ownership percentage.", clarification:"We can see three entities listed \u2014 can you confirm whether Holdco Ltd is the ultimate parent, or if there is a further holding structure above it?" } },
  { id:"1.02", priority:"Medium", section:"Board minutes",
    request:"Minutes of board/senior management meetings during the FY22 to FY25 period.",
    guidance:"These are usually kept by your company secretary or legal team. We need all board and senior management meeting minutes for the full period.",
    example:"PDF or Word documents for each board meeting, typically showing attendees, agenda items, resolutions and any matters arising.",
    fields:["Date","Meeting Type","Attendees","Key Resolutions"],
    matchKeys:["board","minutes","meeting","resolution","secretary"],
    scan:{ ok:false, summary:"We have received minutes for FY24 and FY25 only.", missing:["Board minutes for FY22","Board minutes for FY23"], fix:"Please provide the board minutes for FY22 and FY23 to complete the set." } },
  { id:"1.03", priority:"High", section:"Accounting policies",
    request:"Details of any accounting policy changes which may have occurred over the FY22 to FY25 period (e.g. the application IFRS 15 Revenue from contracts with customers, NZ IFRS 16 Leases). To include the date of change in policy, how it was adopted, and consideration of the impact on the accounts.",
    guidance:"Check the notes to your audited accounts \u2014 accounting policy changes are usually disclosed there. Your auditors or finance team can help identify any changes.",
    example:"A summary table: Policy change | Standard | Date adopted | Impact on P&L | Impact on balance sheet.",
    fields:["Policy Change","Accounting Standard","Date Adopted","P&L Impact","Balance Sheet Impact"],
    matchKeys:["accounting","policy","IFRS","standard","adoption","change"],
    scan:{ ok:true, summary:"Policy note covers all years. IFRS 16 adoption noted with quantified impact.", clarification:"The IFRS 15 transition note mentions \u2018no material impact\u2019 \u2014 can you confirm whether a quantitative assessment was performed?" } },

  /* ── 2. Historical trading ── */
  { id:"2.01", priority:"Critical", section:"Historical management accounts",
    request:"Provide management accounts including monthly income statements, monthly balance sheets and monthly cash flow statements from 31 July 2021 to 30 June 2025.",
    guidance:"Export directly from your accounting system \u2014 Xero, Sage and QuickBooks all have a P&L by month report. We need monthly detail for the full period.",
    example:"An Excel export showing monthly P&L, balance sheet and cash flow for each month from Jul 2021 to Jun 2025.",
    fields:["Month","Revenue","Gross Profit","EBITDA","Net Assets","Cash"],
    matchKeys:["management","monthly","month","mgmt","income","balance","cash flow"],
    scan:{ ok:false, summary:"Monthly P&L received but balance sheets and cash flow statements are missing.", missing:["Monthly balance sheets Jul 2021 \u2013 Jun 2025","Monthly cash flow statements Jul 2021 \u2013 Jun 2025"], fix:"Please export the monthly balance sheet and cash flow reports from your accounting system for the full period." } },
  { id:"2.02", priority:"Medium", section:"Consolidation",
    request:"Consolidation working papers for each FY22 to FY25 financial year including a description for each consolidation adjustment.",
    guidance:"If your group prepares consolidated accounts, we need the working papers showing how entity-level figures roll up. Include intercompany eliminations.",
    example:"An Excel workbook with tabs for each year, showing entity trial balances, intercompany eliminations, and consolidation adjustments with descriptions.",
    fields:["Year","Adjustment Description","Debit","Credit","Entity"],
    matchKeys:["consolidation","consolidated","intercompany","elimination","working paper"],
    scan:null },
  { id:"2.03", priority:"Medium", section:"Quality of earnings",
    request:"Analysis of any earnings adjustments identified (e.g. one-off income or expenses, non-operating income or expenses, non-business related expenses incurred by senior management or shareholders) including back up support and workings for each adjustment. Please include: (i) Value in each month impacted (ii) Associated GL codes.",
    guidance:"Think about any items that are unusual, non-recurring or not reflective of the ongoing business. Your finance team or accountants can help identify these.",
    example:"A schedule showing: Adjustment description | GL code | Monthly values | Total | Rationale for adjustment.",
    fields:["Adjustment","GL Code","Monthly Value","Annual Total","Rationale"],
    matchKeys:["quality","earnings","adjustment","one-off","non-recurring","normalisation"],
    scan:null },
  { id:"2.04", priority:"Medium", section:"Financial statements",
    request:"Audited / special purpose financial statements, if prepared, for FY24 and FY25.",
    guidance:"Request from your auditors if you don\u2019t have copies. PDF format is fine. We need the full statements including notes.",
    example:"Two PDF files \u2014 one per year \u2014 each containing the directors\u2019 report, income statement, balance sheet, cash flow statement and notes.",
    fields:["Year","Revenue","EBITDA","Net Assets","Audit Opinion"],
    matchKeys:["audit","financial","statement","accounts","statutory","annual","special purpose"],
    scan:{ ok:false, summary:"We can see FY24 accounts only. FY25 accounts are missing.", missing:["FY25 audited or special purpose financial statements"], fix:"Please provide the FY25 financial statements once available, or confirm the expected date of completion." } },
  { id:"2.05", priority:"Medium", section:"Financial statements reconciliation",
    request:"A reconciliation of the historical management accounts to the audited or special purpose financial statements, for both the income statement and balance sheet, for FY24 and FY25.",
    guidance:"This shows us how the management accounts tie back to the audited numbers. Usually prepared by your finance team during the audit process.",
    example:"An Excel showing: Management accounts P&L \u2192 Audit adjustments \u2192 Audited P&L, for each line item, for both FY24 and FY25.",
    fields:["Line Item","Mgmt Accounts","Audit Adjustments","Audited Figure","Variance"],
    matchKeys:["reconciliation","reconcile","management","audit","variance","bridge"],
    scan:null },
  { id:"2.06", priority:"Medium", section:"Audit management letters",
    request:"Any reports to Management from the auditors detailing their audit findings, corrected and uncorrected audit adjustments and internal control findings (if audit is performed) for FY24 and FY25.",
    guidance:"These are sometimes called \u2018management letters\u2019 or \u2018audit completion reports\u2019. Request from your auditors if you don\u2019t have copies.",
    example:"PDF letters from the auditors for each year, typically covering: summary of audit approach, key findings, adjustments (corrected and uncorrected), and recommendations.",
    fields:["Year","Finding","Severity","Corrected","Management Response"],
    matchKeys:["audit","management letter","finding","control","adjustment","auditor"],
    scan:{ ok:true, summary:"Management letters for both years received. No material uncorrected misstatements noted." } },
  { id:"2.07", priority:"Critical", section:"Revenue and gross profit by product",
    request:"A breakdown of monthly revenue, cost of sales and gross profit for each service for the FY22 to FY25 period.",
    guidance:"Export from your accounting system by product/service line. If you track revenue by product code or category, that\u2019s what we need.",
    example:"An Excel with monthly columns and rows for each product/service, showing revenue, COGS and gross profit per product per month.",
    fields:["Product/Service","Month","Revenue","Cost of Sales","Gross Profit","Margin %"],
    matchKeys:["revenue","product","service","gross profit","margin","breakdown","segment"],
    scan:{ ok:false, summary:"Revenue breakdown provided but cost of sales is not split by product.", missing:["Cost of sales breakdown by product/service for FY22\u2013FY25"], fix:"Please provide the cost of sales allocated to each product/service line, so we can calculate gross margins by segment.", clarification:"Are cost of sales directly attributable to each service, or is an allocation methodology used?" } },
  { id:"2.08", priority:"Critical", section:"Revenue and gross profit by customer",
    request:"A breakdown of monthly revenue, cost of sales and gross profit for each customer for the FY22 to FY25 period.",
    guidance:"A customer-level revenue report from your accounting or CRM system. Top 20 customers individually, then \u2018other\u2019 is usually sufficient.",
    example:"An Excel showing monthly revenue by customer, with cost of sales and gross profit where available. Top 20 customers named, remainder grouped.",
    fields:["Customer","Month","Revenue","Cost of Sales","Gross Profit"],
    matchKeys:["revenue","customer","client","gross profit","concentration","top"],
    scan:null },
  { id:"2.09", priority:"Critical", section:"Revenue and gross profit by geography",
    request:"A breakdown of monthly revenue, cost of sales and gross profit for each geographic area of operation for the FY22 to FY25 period.",
    guidance:"Split by the geographic regions you operate in. If you only operate in one geography, a short confirmation is sufficient.",
    example:"An Excel with monthly columns, rows grouped by region (e.g. NZ, Australia, UK), showing revenue, COGS and gross profit per region.",
    fields:["Region","Month","Revenue","Cost of Sales","Gross Profit"],
    matchKeys:["revenue","geography","region","country","location","area"],
    scan:null },
  { id:"2.10", priority:"High", section:"Revenue backlog and pipeline",
    request:"Current revenue backlog (contracted but not yet recognised) and sales pipeline, with expected conversion dates and values.",
    guidance:"Your sales or CRM system should have this. Include both signed contracts and qualified pipeline opportunities.",
    example:"A schedule showing: Customer | Contract value | Recognised to date | Backlog | Expected recognition period.",
    fields:["Customer","Contract Value","Recognised","Backlog","Expected Date"],
    matchKeys:["backlog","pipeline","contracted","forecast","order book","sales"],
    isNew:true, scan:null },
  { id:"2.11", priority:"Medium", section:"Capital expenditure",
    request:"A schedule of capital expenditure by asset category for FY22 to FY25, including any committed but not yet incurred capex.",
    guidance:"Your fixed asset register or accounting system should have this detail. Separate maintenance capex from growth capex if possible.",
    example:"An Excel showing: Asset category | FY22 | FY23 | FY24 | FY25 | Committed, split between maintenance and growth.",
    fields:["Asset Category","FY22","FY23","FY24","FY25","Committed"],
    matchKeys:["capex","capital","expenditure","asset","fixed asset","investment"],
    isNew:true, scan:null },

  /* ── 3. Tax & compliance ── */
  { id:"3.01", priority:"Medium", section:"Corporation tax returns",
    request:"Corporation tax returns and computations for FY22 to FY25, including any correspondence with tax authorities.",
    guidance:"Request from your accountants or tax advisors. Include the returns, supporting computations and any HMRC/IRD correspondence.",
    example:"Tax returns for each year plus supporting computation showing adjustments from accounting profit to taxable profit.",
    fields:["Year","Taxable Profit","Tax Paid","Outstanding","Status"],
    matchKeys:["tax","CT600","corporation","HMRC","IRD","computation","return"],
    scan:{ ok:true, summary:"Tax returns for all four years present. Computations attached. No outstanding liabilities noted." } },
  { id:"3.02", priority:"Medium", section:"GST/VAT returns",
    request:"GST or VAT returns for the last 24 months, plus any related correspondence with tax authorities.",
    guidance:"Export from your accounting system or request from your tax advisor. We need the actual filed returns, not just the summary.",
    example:"PDF copies of each GST/VAT return filed over the last 24 months, plus any assessment notices received.",
    fields:["Period","GST/VAT Collected","GST/VAT Paid","Net Position","Filed"],
    matchKeys:["GST","VAT","indirect","tax","return","sales tax"],
    isNew:true, scan:null },
  { id:"3.03", priority:"High", section:"Employee tax obligations",
    request:"Confirmation that all PAYE, KiwiSaver/superannuation and other employee-related tax obligations are up to date for FY22\u2013FY25.",
    guidance:"Your payroll provider or accountant can confirm this. We need evidence that all employee-related taxes have been filed and paid on time.",
    example:"A letter from your payroll provider or accountant confirming all PAYE, KiwiSaver and FBT obligations are current.",
    fields:["Obligation","Period","Amount","Status"],
    matchKeys:["PAYE","payroll","KiwiSaver","superannuation","employee","FBT","withholding"],
    isNew:true, scan:null },
];

const SECTIONS = [
  { key:"1", label:"Business context" },
  { key:"2", label:"Historical trading" },
  { key:"3", label:"Tax & compliance" },
];

const QA_AREAS = ["Revenue","OPEX","Working Capital","Cash Flow","Tax","Capex","Employee Costs","Related Parties","Forecasts","Revenue by Customer"];

const QA_INITIAL = [
  { id:"qa-1", area:"Revenue", question:"Can you explain the key drivers behind the 12% revenue growth in FY24? Was this organic or driven by new customer acquisition?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-2", area:"Revenue by Customer", question:"Your top 5 customers represent 43% of revenue. Have any of these customers indicated changes to their contract terms or volume commitments?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-3", area:"OPEX", question:"Staff costs increased 18% YoY but headcount only grew 8%. What is driving the per-head cost increase — wage inflation, contractor mix, or something else?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-4", area:"Working Capital", question:"Trade receivables days have increased from 42 to 58 days over FY23–FY24. Is this due to a change in payment terms, customer mix, or collection issues?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-5", area:"Cash Flow", question:"Free cash flow conversion was only 10.5% of EBITDA in FY24. What are the main working capital or capex items absorbing cash, and is this expected to normalise?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-6", area:"Tax", question:"We note a NZ$180k deferred tax asset on the FY24 balance sheet. Can you confirm what this relates to and the expected timing of realisation?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-7", area:"Capex", question:"FY25 capex is forecast at 2.4x the FY22–FY24 average. Is this a one-off investment or the start of a new run-rate? Please provide the business case.", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-8", area:"Employee Costs", question:"Are there any retention or change-of-control bonus arrangements in place for key employees that would be triggered by this transaction?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-9", area:"Related Parties", question:"Management fees of NZ$240k p.a. are paid to a related entity. Can you confirm the nature of services provided and whether this would continue post-transaction?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-10", area:"Forecasts", question:"The FY25 forecast assumes 26% EBITDA growth. Can you provide a bridge from FY24 actuals to FY25 forecast EBITDA showing the key assumptions?", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-11", area:"Revenue", question:"What proportion of FY24 revenue was recurring (contracted/subscription) vs. project-based or ad-hoc? Please provide supporting detail.", status:"pending", answer:"", aiReview:null, submitCount:0 },
  { id:"qa-12", area:"OPEX", question:"Are there any cost items within operating expenses that relate to the shareholders personally rather than the ongoing business (e.g. vehicles, travel, insurance)?", status:"pending", answer:"", aiReview:null, submitCount:0 },
];

var ONBOARD_TIPS = [
  { target: "kanban-todo", text: "Requests from your advisor live here, sorted by priority.", pos: "below" },
  { target: "upload-area", text: "Open each request and upload the relevant document.", pos: "left" },
  { target: "chat-row", text: "If anything is unclear, you can chat with Dilli — your AI assistant will help explain what's needed.", pos: "left" },
  { target: "template-link", text: "We've prepared templates as a reference so you know what to expect. You don't need to follow them exactly.", pos: "left" },
  { target: "notes-area", text: "Can't find a file, or want to add context? Leave a note for your advisor here.", pos: "left" },
  { target: "new-badge", text: "Your advisor may add more requests over time — they'll appear with a blue \"new\" tag.", pos: "below" },
  { target: "view-toggle", text: "You can switch between board and list view at any time.", pos: "below" },
  { target: "bulk-upload", text: "We verify every upload automatically. Once roughly 80% of requests are fulfilled, we'll begin our detailed review. You can also bulk-upload from your dataroom here.", pos: "below" },
];

/* ════════════════════════════════════════
   COMPONENT
════════════════════════════════════════ */
export default function VendorPortal() {
  var navigate = useNavigate();
  var [view, setView] = useState("home");
  var [selected, setSelected] = useState(null);
  var [files, setFiles] = useState({});
  var [scanning, setScanning] = useState({});
  var [scanned, setScanned] = useState({});
  var [boardView, setBoardView] = useState("kanban");
  var [dillyMsgs, setDilliMsgs] = useState([]);
  var [dillyInput, setDilliInput] = useState("");
  var [notes, setNotes] = useState({});
  var [finSystems, setFinSystems] = useState([]);
  var [finCustom, setFinCustom] = useState("");
  var [yearEnd, setYearEnd] = useState("");
  var [showBulk, setShowBulk] = useState(true);
  var [bulkMatches, setBulkMatches] = useState(null);
  var [bulkScanning, setBulkScanning] = useState(false);
  var [dragBulk, setDragBulk] = useState(false);
  var [homeStage, setHomeStage] = useState(0);
  var [chatOpen, setChatOpen] = useState(false);
  var [onboardStep, setOnboardStep] = useState(0); // 0=show step1, 1=step2, 2=step3, 3=done
  var [onboardDismissed, setOnboardDismissed] = useState(false);
  var [tipRect, setTipRect] = useState(null);
  var [qaItems, setQaItems] = useState(QA_INITIAL.map(function(q) { return Object.assign({}, q); }));
  var [selectedQa, setSelectedQa] = useState(null);
  var [qaFilter, setQaFilter] = useState("all");
  var [qaAnswer, setQaAnswer] = useState("");
  var [qaReviewing, setQaReviewing] = useState(false);
  var [qaBulkFiles, setQaBulkFiles] = useState(null);
  var [qaBulkScanning, setQaBulkScanning] = useState(false);
  var [dragQaBulk, setDragQaBulk] = useState(false);
  var bulkRef = useRef(null);
  var qaBulkRef = useRef(null);
  var chatEnd = useRef(null);

  useEffect(function() {
    if (view !== "home") return;
    var t1 = setTimeout(function() { setHomeStage(1); }, 300);
    var t2 = setTimeout(function() { setHomeStage(2); }, 700);
    return function() { clearTimeout(t1); clearTimeout(t2); };
  }, [view]);

  useEffect(function() {
    if (chatEnd.current) chatEnd.current.scrollIntoView({ behavior:"smooth" });
  }, [dillyMsgs]);

  /* Reset onboarding every time we land on the RFI view */
  useEffect(function() {
    if (view === "rfi") {
      setOnboardStep(0);
      setOnboardDismissed(false);
      setTipRect(null);
    }
  }, [view]);

  useEffect(function() {
    if (onboardDismissed || onboardStep >= ONBOARD_TIPS.length) { setTipRect(null); return; }
    if (view !== "rfi") return;

    /* ── Side-effects: open/close panel per step ── */
    var step = onboardStep;
    var needsPanel = step >= 1 && step <= 4;  // steps 1-4 require detail panel open
    var revenueItem = ITEMS.find(function(i) { return i.id === "2.07"; });

    if (needsPanel) {
      // Open Revenue item if not already selected
      if (revenueItem && (!selected || selected.id !== revenueItem.id)) {
        setSelected(revenueItem);
        setDilliInput("");
        setDilliMsgs([]);
      }
      setChatOpen(false); // ensure chat is collapsed so chat-row is visible
    }
    if (!needsPanel && selected) {
      setSelected(null); // close panel for kanban/nav steps
    }

    /* ── Scroll into view, then measure ── */
    var delay = needsPanel && (!selected || selected.id !== (revenueItem && revenueItem.id)) ? 500 : 300;
    var t1, t2;
    t1 = setTimeout(function() {
      var el = document.querySelector('[data-onboard="' + ONBOARD_TIPS[step].target + '"]');
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, delay);
    var measure = function() {
      var el = document.querySelector('[data-onboard="' + ONBOARD_TIPS[step].target + '"]');
      if (!el) { setTipRect(null); return; }
      var r = el.getBoundingClientRect();
      setTipRect({ top: r.top, left: r.left, width: r.width, height: r.height, pos: ONBOARD_TIPS[step].pos });
    };
    t2 = setTimeout(measure, delay + 400);
    window.addEventListener("resize", measure);
    return function() { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", measure); };
  }, [onboardStep, onboardDismissed, view, selected]);

  /* Auto-derived status */
  var getStatus = function(id) {
    if (scanning[id]) return "reviewing";
    if (scanned[id]) {
      var item = ITEMS.find(function(i) { return i.id === id; });
      return item && item.scan && item.scan.ok ? "complete" : "attention";
    }
    if (files[id]) return "uploaded";
    return "open";
  };

  var total = ITEMS.length;
  var done = ITEMS.filter(function(i) { return getStatus(i.id) === "complete"; }).length;
  var attn = ITEMS.filter(function(i) { return getStatus(i.id) === "attention"; }).length;
  var newCount = ITEMS.filter(function(i) { return i.isNew; }).length;

  var uploadFile = function(id, name) {
    setFiles(function(f) { return Object.assign({}, f, { [id]: name }); });
    setScanning(function(s) { return Object.assign({}, s, { [id]: true }); });
    setTimeout(function() {
      setScanning(function(s) { return Object.assign({}, s, { [id]: false }); });
      setScanned(function(s) { return Object.assign({}, s, { [id]: true }); });
    }, 2400);
  };

  var openItem = function(item) {
    setSelected(item);
    setDilliInput("");
    setDilliMsgs([]);
    setChatOpen(false);
  };

  var sendDilli = function(q) {
    if (!q.trim() || !selected) return;
    var lower = q.trim().toLowerCase();
    var reply = selected.guidance;
    if (lower.includes("template")) {
      reply = "Here's the template for \"" + selected.section + "\". But remember, you don't have to use our template. Just upload the files you already have. The template is only a reference if it's completely unclear what we're after. You don't need to change or reformat your files for us.";
      dlTemplate(selected);
    }
    setDilliMsgs(function(m) {
      return m.concat([{ from:"user", text:q }, { from:"dilly", text:reply }]);
    });
    setDilliInput("");
  };

  var dlTemplate = function(item) {
    var csv = item.fields.join(",") + "\n";
    var a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
    a.download = item.id + "_template.csv";
    a.click();
  };

  var matchFilesToItems = function(fileList) {
    var results = [], used = {};
    Array.from(fileList).forEach(function(file) {
      var name = file.name.toLowerCase();
      var best = null, bestS = 0;
      ITEMS.forEach(function(item) {
        if (files[item.id] || used[item.id]) return;
        var s = 0;
        item.matchKeys.forEach(function(k) { if (name.includes(k.toLowerCase())) s++; });
        if (name.includes(item.section.toLowerCase().split(" ")[0])) s += 0.5;
        if (s > bestS) { bestS = s; best = item; }
      });
      if (best && bestS > 0) used[best.id] = true;
      results.push({ file:file.name, match:best, score:bestS });
    });
    return results;
  };

  var handleBulkUpload = function(fileList) {
    setBulkScanning(true);
    setTimeout(function() {
      var matches = matchFilesToItems(fileList);
      setBulkMatches(matches);
      setBulkScanning(false);
      matches.forEach(function(m) { if (m.match && m.score > 0) uploadFile(m.match.id, m.file); });
    }, 2200);
  };

  var handleQaBulkUpload = function(fileList) {
    setQaBulkScanning(true);
    setTimeout(function() {
      var fileNames = Array.from(fileList).map(function(f) { return f.name; });
      var matched = [];
      fileNames.forEach(function(name) {
        var lower = name.toLowerCase();
        var best = null, bestS = 0;
        qaItems.forEach(function(q) {
          var s = 0;
          var areaLower = q.area.toLowerCase();
          if (lower.includes(areaLower.split(" ")[0])) s += 2;
          areaLower.split(" ").forEach(function(w) { if (w.length > 3 && lower.includes(w)) s++; });
          if (s > bestS) { bestS = s; best = q; }
        });
        matched.push({ file: name, match: best, score: bestS });
      });
      setQaBulkFiles(matched);
      setQaBulkScanning(false);
    }, 1800);
  };

  /* Helpers */
  var DilliDot = function() {
    return (
      <div style={{ width:24, height:24, borderRadius:"50%", background:T.accentLight, border:"1px solid " + T.accentMid + "44", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <span style={{ fontFamily:SERIF, fontSize:12, fontWeight:500, color:T.accent, lineHeight:1 }}>D</span>
      </div>
    );
  };

  var statusLabel = { open:"To do", reviewing:"Reviewing\u2026", uploaded:"Uploaded", complete:"Complete", attention:"Needs attention" };
  var statusDot = { open:T.mutedLight, reviewing:T.accent, uploaded:T.accent, complete:T.success, attention:T.warn };

  /* ════ HOME ════ */
  if (view === "home") {
    return (
      <div style={{ fontFamily:SANS, minHeight:"100vh", display:"flex", background:T.white, position:"relative" }}>
        <style dangerouslySetInnerHTML={{ __html:CSS }} />
        <button onClick={function() { navigate("/"); }} style={{ position:"absolute", top:24, right:24, fontSize:10, padding:"4px 12px", borderRadius:4, border:"1px dashed #d97706", color:"#d97706", background:"none", cursor:"pointer", fontFamily:SANS, fontWeight:500, zIndex:10 }}>{"\u21C4"} Advisor View</button>

        {/* Left */}
        <div style={{ flex:"0 0 52%", display:"flex", flexDirection:"column", justifyContent:"center", padding:"0 72px 0 80px" }}>
          <div style={{ animation:"fadeUp .5s ease both" }}>
            {/* Dilli mark */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:36 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="3" width="3" height="18" rx="1.5" fill={T.ink} />
                <rect x="7" y="6" width="3" height="12" rx="1.5" fill={T.accent} />
              </svg>
              <span style={{ fontSize:11, fontWeight:700, color:T.muted, letterSpacing:1.8, textTransform:"uppercase" }}>Dilli</span>
            </div>
            <h1 style={{ fontFamily:SERIF, fontSize:54, fontWeight:400, color:T.ink, lineHeight:1.1, margin:"0 0 24px", letterSpacing:-.5 }}>
              Welcome to<br />Project Alpha
            </h1>
            <p style={{ fontSize:15, color:T.muted, lineHeight:1.85, maxWidth:440, margin:"0 0 48px", opacity:homeStage >= 1 ? 1 : 0, transition:"opacity .5s" }}>
              Dilli, your AI assistant, will guide you through the due diligence process — from understanding what's needed to uploading documents and answering questions.
            </p>
            <div style={{ display:"flex", gap:16, alignItems:"center", opacity:homeStage >= 2 ? 1 : 0, transition:"opacity .5s" }}>
              <button onClick={function() { setView("process"); }}
                style={{ padding:"10px 24px", borderRadius:20, background:T.ink, color:T.white, fontSize:13, fontWeight:500, border:"none", cursor:"pointer", fontFamily:SANS, transition:"background .15s" }}
                onMouseEnter={function(e) { e.currentTarget.style.background = T.inkSoft; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = T.ink; }}>
                Get started
              </button>
              <span style={{ fontSize:12, color:T.mutedLight }}>{total} requests</span>
            </div>
          </div>
        </div>

        {/* Right — decorative panel */}
        <div style={{ flex:"0 0 48%", background:T.bg, position:"relative", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
          {/* Subtle grid pattern */}
          <div style={{ position:"absolute", inset:0, opacity:.06, backgroundImage:"linear-gradient(" + T.ink + " 1px, transparent 1px), linear-gradient(90deg, " + T.ink + " 1px, transparent 1px)", backgroundSize:"48px 48px" }} />
          {/* Large accent circle */}
          <div style={{ position:"absolute", width:420, height:420, borderRadius:"50%", border:"1px solid " + T.accentMid + "30", top:"50%", left:"50%", transform:"translate(-50%,-50%)", animation:"orbFloat 8s ease-in-out infinite" }} />
          <div style={{ position:"absolute", width:300, height:300, borderRadius:"50%", border:"1px solid " + T.accentMid + "20", top:"50%", left:"50%", transform:"translate(-50%,-50%)" }} />
          {/* Floating cards */}
          <div style={{ position:"relative", zIndex:1 }}>
            {/* Card 1 — looks like a request card */}
            <div style={{ width:260, background:T.white, borderRadius:10, padding:"18px 20px", boxShadow:"0 8px 40px rgba(0,0,0,.06)", marginBottom:14, animation:"fadeUp .7s ease both", animationDelay:".3s" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:T.err }} />
                <span style={{ fontSize:10, color:T.muted }}>2.01</span>
                <span style={{ marginLeft:"auto", fontSize:9, fontWeight:600, color:T.success, background:"#ECFDF5", padding:"2px 7px", borderRadius:4 }}>Complete</span>
              </div>
              <div style={{ fontSize:12, fontWeight:600, color:T.ink, marginBottom:4 }}>Management accounts</div>
              <div style={{ height:3, borderRadius:2, background:T.border, marginTop:8 }}>
                <div style={{ height:3, borderRadius:2, background:T.success, width:"100%" }} />
              </div>
            </div>
            {/* Card 2 */}
            <div style={{ width:260, background:T.white, borderRadius:10, padding:"18px 20px", boxShadow:"0 8px 40px rgba(0,0,0,.06)", marginLeft:30, animation:"fadeUp .7s ease both", animationDelay:".5s" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:T.warn }} />
                <span style={{ fontSize:10, color:T.muted }}>1.03</span>
                <span style={{ marginLeft:"auto", fontSize:9, fontWeight:600, color:T.warn, background:"#FFFBEB", padding:"2px 7px", borderRadius:4 }}>Needs attention</span>
              </div>
              <div style={{ fontSize:12, fontWeight:600, color:T.ink, marginBottom:4 }}>Accounting policies</div>
              <div style={{ height:3, borderRadius:2, background:T.border, marginTop:8 }}>
                <div style={{ height:3, borderRadius:2, background:T.warn, width:"60%" }} />
              </div>
            </div>
            {/* Card 3 */}
            <div style={{ width:260, background:T.white, borderRadius:10, padding:"18px 20px", boxShadow:"0 8px 40px rgba(0,0,0,.06)", marginTop:14, animation:"fadeUp .7s ease both", animationDelay:".7s" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:T.accent }} />
                <span style={{ fontSize:10, color:T.muted }}>2.07</span>
                <span style={{ marginLeft:"auto", fontSize:9, fontWeight:700, color:"#3B82F6", background:"#EFF6FF", padding:"2px 7px", borderRadius:4 }}>New</span>
              </div>
              <div style={{ fontSize:12, fontWeight:600, color:T.ink, marginBottom:4 }}>Revenue analysis</div>
              <div style={{ height:3, borderRadius:2, background:T.border, marginTop:8 }}>
                <div style={{ height:3, borderRadius:2, background:T.mutedLight, width:"0%" }} />
              </div>
            </div>
          </div>
          {/* Corner accent */}
          <div style={{ position:"absolute", bottom:40, right:40, fontSize:11, color:T.mutedLight, fontFamily:SERIF, letterSpacing:1 }}>Financial Due Diligence</div>
        </div>
      </div>
    );
  }

  /* ════ PROCESS OVERVIEW ════ */
  if (view === "process") {
    var STEPS = [
      { num:"01", title:"Information Request List (this)", text:"Your advisor has prepared an initial list of documents and data they need to understand your business. This is prepared before we know your business in detail, so it's broader than it might eventually need to be. That's normal." },
      { num:"02", title:"You provide what's available", text:"Work through the list and upload what you have readily available. You don't need perfect or complete information. If something is hard to access, just let us know. We'll flag it to the advisor and work out how to obtain it." },
      { num:"03", title:"We review your financials", text:"Once we receive your documents, we'll review them in detail. We'll develop a much clearer picture of your business and will follow up with targeted questions. This follow-up is normal and doesn't indicate a problem." },
      { num:"04", title:"We cross-reference and ask about variances", text:"We'll compare figures across documents and periods. Where we spot variances we'll ask you about them and, importantly, we'll suggest possible reasons. You just need to confirm or correct our read. This is standard FDD practice." },
      { num:"05", title:"Report", text:"The findings are compiled into an FDD report for the transaction. Your cooperation through this process directly affects the quality and speed of the outcome." },

    ];
    return (
      <div style={{ fontFamily:SANS, minHeight:"100vh", background:T.bg, display:"flex", justifyContent:"center", position:"relative" }}>
        <style dangerouslySetInnerHTML={{ __html:CSS }} />
        <button onClick={function() { navigate("/"); }} style={{ position:"absolute", top:24, right:24, fontSize:10, padding:"4px 12px", borderRadius:4, border:"1px dashed #d97706", color:"#d97706", background:"none", cursor:"pointer", fontFamily:SANS, fontWeight:500, zIndex:10 }}>{"\u21C4"} Advisor View</button>
        <div style={{ maxWidth:720, width:"100%", padding:"60px 40px 80px", animation:"fadeUp .5s ease both" }}>

          {/* Top rule */}
          <div style={{ height:3, background:T.ink, marginBottom:48 }} />

          <h1 style={{ fontFamily:SERIF, fontSize:44, fontWeight:400, color:T.ink, lineHeight:1.15, margin:"0 0 56px", letterSpacing:-.3 }}>
            What to expect from this process
          </h1>

          {/* Timeline */}
          <div style={{ position:"relative", paddingLeft:68 }}>
            {/* Vertical connector line */}
            <div style={{ position:"absolute", left:23, top:24, bottom:80, width:1, background:T.border }} />

            {STEPS.map(function(step, i) {
              return (
                <div key={step.num} style={{ position:"relative", marginBottom:i < STEPS.length - 1 ? 52 : 0, animation:"fadeUp .4s ease both", animationDelay:(i * 0.08) + "s" }}>
                  {/* Number circle */}
                  <div style={{
                    position:"absolute", left:-68, top:0,
                    width:47, height:47, borderRadius:"50%", background:T.ink,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    zIndex:2,
                  }}>
                    <span style={{ fontSize:14, fontWeight:600, color:T.white, fontFamily:SANS }}>{step.num}</span>
                  </div>

                  <h3 style={{ fontSize:20, fontWeight:700, color:T.ink, margin:"0 0 12px", lineHeight:1.3, paddingTop:10 }}>{step.title}</h3>
                  <p style={{ fontSize:15, color:T.inkSoft, lineHeight:1.75, margin:0 }}>{step.text}</p>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div style={{ marginTop:64 }}>
            <button onClick={function() { setView("setup"); }}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"12px 28px", borderRadius:24, background:T.ink, color:T.white, fontSize:14, fontWeight:500, border:"none", cursor:"pointer", fontFamily:SANS }}
              onMouseEnter={function(e) { e.currentTarget.style.background = T.inkSoft; }}
              onMouseLeave={function(e) { e.currentTarget.style.background = T.ink; }}>
              Continue
              <span style={{ fontSize:18 }}>{"\u2192"}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ════ SETUP / INTAKE QUESTIONS ════ */
  if (view === "setup") {
    var SYS_OPTIONS = ["Xero", "MYOB", "QuickBooks", "SAP", "Oracle", "Sage", "NetSuite"];
    // Popular FYE months shown as chips; rest in dropdown

    var toggleSystem = function(name) {
      setFinSystems(function(prev) {
        return prev.includes(name) ? prev.filter(function(s) { return s !== name; }) : prev.concat([name]);
      });
    };

    var canContinue = (finSystems.length > 0 || finCustom.trim() !== "") && yearEnd !== "";

    return (
      <div style={{ fontFamily:SANS, minHeight:"100vh", background:T.bg, display:"flex", justifyContent:"center", position:"relative" }}>
        <style dangerouslySetInnerHTML={{ __html:CSS }} />
        <button onClick={function() { navigate("/"); }} style={{ position:"absolute", top:24, right:24, fontSize:10, padding:"4px 12px", borderRadius:4, border:"1px dashed #d97706", color:"#d97706", background:"none", cursor:"pointer", fontFamily:SANS, fontWeight:500, zIndex:10 }}>{"\u21C4"} Advisor View</button>
        <div style={{ maxWidth:620, width:"100%", padding:"60px 40px 80px", animation:"fadeUp .5s ease both" }}>

          {/* Top rule */}
          <div style={{ height:3, background:T.ink, marginBottom:48 }} />

          <h1 style={{ fontFamily:SERIF, fontSize:40, fontWeight:400, color:T.ink, lineHeight:1.15, margin:"0 0 12px", letterSpacing:-.3 }}>
            A couple of things before we start
          </h1>
          <p style={{ fontSize:15, color:T.inkSoft, lineHeight:1.7, margin:"0 0 48px" }}>
            This helps us tailor the request list and verify your documents more accurately.
          </p>

          {/* Question 1: Financial system */}
          <div style={{ marginBottom:40 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, color:T.ink, marginBottom:6, letterSpacing:.2 }}>
              What financial system does the business use?
            </label>
            <p style={{ fontSize:13, color:T.muted, margin:"0 0 16px", lineHeight:1.5 }}>
              Select all that apply.
            </p>

            <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginBottom:16 }}>
              {SYS_OPTIONS.map(function(name) {
                var active = finSystems.includes(name);
                return (
                  <button key={name} onClick={function() { toggleSystem(name); }}
                    style={{
                      padding:"9px 20px", borderRadius:20,
                      border: active ? "1.5px solid " + T.ink : "1.5px solid " + T.border,
                      background: active ? T.ink : T.white,
                      color: active ? T.white : T.inkSoft,
                      fontSize:13, fontWeight: active ? 600 : 400,
                      cursor:"pointer", fontFamily:SANS,
                      transition:"all .15s ease",
                    }}
                    onMouseEnter={function(e) { if (!active) e.currentTarget.style.borderColor = T.inkSoft; }}
                    onMouseLeave={function(e) { if (!active) e.currentTarget.style.borderColor = T.border; }}>
                    {name}
                  </button>
                );
              })}
            </div>

            <input
              type="text"
              placeholder="Other system (type here)"
              value={finCustom}
              onChange={function(e) { setFinCustom(e.target.value); }}
              style={{
                width:"100%", padding:"11px 16px", borderRadius:10,
                border:"1.5px solid " + T.border, fontSize:13, color:T.ink,
                background:T.white, fontFamily:SANS, boxSizing:"border-box",
                transition:"border-color .15s",
              }}
              onFocus={function(e) { e.target.style.borderColor = T.ink; }}
              onBlur={function(e) { e.target.style.borderColor = T.border; }}
            />
          </div>

          {/* Question 2: Financial year-end */}
          <div style={{ marginBottom:48 }}>
            <label style={{ display:"block", fontSize:13, fontWeight:600, color:T.ink, marginBottom:6, letterSpacing:.2 }}>
              When is the financial year-end?
            </label>
            <p style={{ fontSize:13, color:T.muted, margin:"0 0 16px", lineHeight:1.5 }}>
              Select the month in which the financial year ends.
            </p>

            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"nowrap" }}>
              {["Mar","Dec","Jun","Sep"].map(function(m) {
                var full = { Mar:"March", Dec:"December", Jun:"June", Sep:"September" }[m];
                var active = yearEnd === full;
                return (
                  <button key={m} onClick={function() { setYearEnd(full); }}
                    style={{
                      padding:"9px 20px", borderRadius:20,
                      border: active ? "1.5px solid " + T.ink : "1.5px solid " + T.border,
                      background: active ? T.ink : T.white,
                      color: active ? T.white : T.inkSoft,
                      fontSize:13, fontWeight: active ? 600 : 400,
                      cursor:"pointer", fontFamily:SANS,
                      transition:"all .15s ease", whiteSpace:"nowrap",
                    }}
                    onMouseEnter={function(e) { if (!active) e.currentTarget.style.borderColor = T.inkSoft; }}
                    onMouseLeave={function(e) { if (!active) e.currentTarget.style.borderColor = T.border; }}>
                    {m}
                  </button>
                );
              })}

              {/* Other months dropdown */}
              <div style={{ position:"relative" }}>
                <select
                  value={["March","December","June","September"].includes(yearEnd) ? "" : yearEnd}
                  onChange={function(e) { if (e.target.value) setYearEnd(e.target.value); }}
                  style={{
                    padding:"9px 32px 9px 16px", borderRadius:20,
                    border: "1.5px solid " + (yearEnd && !["March","December","June","September"].includes(yearEnd) ? T.ink : T.border),
                    background: yearEnd && !["March","December","June","September"].includes(yearEnd) ? T.ink : T.white,
                    color: yearEnd && !["March","December","June","September"].includes(yearEnd) ? T.white : T.inkSoft,
                    fontSize:13, fontWeight: yearEnd && !["March","December","June","September"].includes(yearEnd) ? 600 : 400,
                    cursor:"pointer", fontFamily:SANS,
                    appearance:"none", WebkitAppearance:"none",
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%234A5568' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                    backgroundRepeat:"no-repeat",
                    backgroundPosition:"right 12px center",
                    transition:"all .15s ease",
                  }}>
                  <option value="">Other…</option>
                  {["January","February","April","May","July","August","October","November"].map(function(m) {
                    return <option key={m} value={m}>{m}</option>;
                  })}
                </select>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button onClick={function() { if (canContinue) setView("rfi"); }}
            style={{
              display:"flex", alignItems:"center", gap:8,
              padding:"12px 28px", borderRadius:24,
              background: canContinue ? T.ink : T.border,
              color: canContinue ? T.white : T.muted,
              fontSize:14, fontWeight:500, border:"none",
              cursor: canContinue ? "pointer" : "default",
              fontFamily:SANS, transition:"all .2s ease",
            }}
            onMouseEnter={function(e) { if (canContinue) e.currentTarget.style.background = T.inkSoft; }}
            onMouseLeave={function(e) { if (canContinue) e.currentTarget.style.background = T.ink; }}>
            Continue to RFI
            <span style={{ fontSize:18 }}>{"\u2192"}</span>
          </button>

          <button onClick={function() { setView("process"); }}
            style={{ marginTop:16, fontSize:12, color:T.muted, background:"none", border:"none", cursor:"pointer", fontFamily:SANS, textDecoration:"underline", textUnderlineOffset:3 }}>
            Back
          </button>
        </div>
      </div>
    );
  }

  /* ════ Q&A VIEW ════ */
  if (view === "qa") {
    var qaPending = qaItems.filter(function(q) { return q.status === "pending"; }).length;
    var qaAnswered = qaItems.filter(function(q) { return q.status === "answered" || q.status === "needs-detail"; }).length;
    var qaApproved = qaItems.filter(function(q) { return q.status === "approved"; }).length;

    var filteredQa = qaFilter === "all" ? qaItems : qaItems.filter(function(q) { return q.status === qaFilter; });

    // Group by area
    var qaByArea = {};
    filteredQa.forEach(function(q) {
      if (!qaByArea[q.area]) qaByArea[q.area] = [];
      qaByArea[q.area].push(q);
    });

    var handleQaSubmit = function() {
      if (!selectedQa || !qaAnswer.trim()) return;
      if (qaAnswer.trim().length < 20) {
        setQaItems(function(items) {
          return items.map(function(q) {
            if (q.id !== selectedQa.id) return q;
            return Object.assign({}, q, { answer: qaAnswer, status: "needs-detail", aiReview: { status: "needs-detail", feedback: "Answer is too short. Please provide more detail to adequately address this question." } });
          });
        });
        setSelectedQa(function(prev) {
          return prev ? Object.assign({}, prev, { answer: qaAnswer, status: "needs-detail", aiReview: { status: "needs-detail", feedback: "Answer is too short. Please provide more detail to adequately address this question." } }) : prev;
        });
        return;
      }

      setQaReviewing(true);
      var currentId = selectedQa.id;
      var currentCount = selectedQa.submitCount || 0;

      setTimeout(function() {
        var isApproved = currentCount >= 1;
        var review = isApproved
          ? { status: "approved", feedback: "Answer addresses the question with sufficient detail." }
          : { status: "needs-detail", feedback: getQaFeedback(selectedQa.area) };
        var newStatus = isApproved ? "approved" : "needs-detail";

        setQaItems(function(items) {
          return items.map(function(q) {
            if (q.id !== currentId) return q;
            return Object.assign({}, q, { answer: qaAnswer, status: newStatus, aiReview: review, submitCount: currentCount + 1 });
          });
        });
        setSelectedQa(function(prev) {
          if (!prev || prev.id !== currentId) return prev;
          return Object.assign({}, prev, { answer: qaAnswer, status: newStatus, aiReview: review, submitCount: currentCount + 1 });
        });
        setQaReviewing(false);
      }, 1500);
    };

    var getQaFeedback = function(area) {
      var feedbacks = {
        "Revenue": "Please clarify the time period and provide supporting revenue figures.",
        "Revenue by Customer": "Can you include specific contract renewal dates or customer churn data?",
        "OPEX": "Please break down the cost increase by category (wages, contractors, other).",
        "Working Capital": "Can you provide an aged receivables breakdown to support this?",
        "Cash Flow": "Please quantify the main cash flow items and expected normalisation timeline.",
        "Tax": "Can you attach the relevant tax computation or assessment notice?",
        "Capex": "Please provide the specific business case or investment proposal document.",
        "Employee Costs": "Can you confirm the total value of any retention or bonus arrangements?",
        "Related Parties": "Please provide the related party agreement or terms of service.",
        "Forecasts": "Can you provide a monthly build-up of the forecast assumptions?",
      };
      return feedbacks[area] || "Please provide more specific detail to fully address this question.";
    };

    var qaStatusDot = { pending: T.mutedLight, answered: T.accent, "needs-detail": T.warn, approved: T.success };
    var qaStatusLabel = { pending: "Pending", answered: "Answered", "needs-detail": "Needs detail", approved: "Approved" };

    return (
      <div style={{ fontFamily: SANS, minHeight: "100vh", background: T.bg }}>
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        {/* Nav — same as RFI */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 32px", height: 56, background: T.white, borderBottom: "1px solid " + T.border, position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="3" width="3" height="18" rx="1.5" fill={T.ink} />
              <rect x="7" y="6" width="3" height="12" rx="1.5" fill={T.accent} />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.ink, letterSpacing: 1.5, textTransform: "uppercase" }}>Dilli</span>
            <span style={{ color: T.border, margin: "0 4px" }}>/</span>
            <span style={{ fontSize: 12, color: T.muted }}>Project Alpha</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 11, color: T.muted }}>{qaApproved}/{qaItems.length} approved</span>
            <div style={{ width: 80, height: 2, background: T.border, borderRadius: 1, overflow: "hidden" }}>
              <div style={{ height: "100%", width: (qaApproved / qaItems.length * 100) + "%", background: T.success, transition: "width .4s" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.mutedLight }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: T.mutedLight, display: "inline-block" }} />{qaPending} Pending</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.warn }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: T.warn, display: "inline-block" }} />{qaAnswered} Answered</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.success }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: T.success, display: "inline-block" }} />{qaApproved} Approved</span>
            </div>
            <div style={{ display: "flex", border: "1px solid " + T.border, borderRadius: 5, overflow: "hidden", marginLeft: 4 }}>
              {[["rfi", "RFI"], ["qa", "Q&A"]].map(function(v) {
                var on = view === v[0];
                return <button key={v[0]} onClick={function() { setView(v[0]); setSelected(null); setSelectedQa(null); }} style={{ padding: "5px 14px", fontSize: 11, fontWeight: on ? 600 : 400, background: on ? T.ink : T.white, color: on ? T.white : T.muted, border: "none", cursor: "pointer", fontFamily: SANS }}>{v[1]}</button>;
              })}
            </div>
            <button onClick={function() { setView("home"); setSelectedQa(null); }} style={{ fontSize: 11, color: T.muted, background: "none", border: "none", cursor: "pointer", fontFamily: SANS }}>Home</button>
            <button onClick={function() { navigate("/"); }} style={{ fontSize: 10, padding: "4px 12px", borderRadius: 4, border: "1px dashed #d97706", color: "#d97706", background: "none", cursor: "pointer", fontFamily: SANS, fontWeight: 500 }}>{"\u21C4"} Advisor View</button>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.ink, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.accentMid }}>M</span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", height: "calc(100vh - 56px)" }}>

          {/* Left: question list */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

            {/* Bulk upload from dataroom */}
            <div
              onDragOver={function(e) { e.preventDefault(); setDragQaBulk(true); }}
              onDragLeave={function() { setDragQaBulk(false); }}
              onDrop={function(e) { e.preventDefault(); setDragQaBulk(false); if (e.dataTransfer.files.length) handleQaBulkUpload(e.dataTransfer.files); }}
              onClick={function() { if (!qaBulkScanning && !qaBulkFiles) qaBulkRef.current && qaBulkRef.current.click(); }}
              style={{ marginBottom:16, padding:"14px 20px", borderRadius:8, background:T.white, border:dragQaBulk ? "1.5px solid " + T.accent : "1.5px dashed " + T.border, cursor:qaBulkScanning ? "default" : "pointer", transition:"all .15s" }}>
              <input ref={qaBulkRef} type="file" multiple style={{ display:"none" }} onChange={function(e) { if (e.target.files.length) handleQaBulkUpload(e.target.files); }} />

              {qaBulkScanning ? (
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:14, height:14, border:"2px solid " + T.accent, borderTopColor:"transparent", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
                  <span style={{ fontSize:13, color:T.ink }}>Matching files to questions…</span>
                </div>
              ) : qaBulkFiles ? (
                <div onClick={function(e) { e.stopPropagation(); }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:T.ink }}>Matched {qaBulkFiles.filter(function(m) { return m.score > 0; }).length} of {qaBulkFiles.length} files</span>
                    <button onClick={function() { setQaBulkFiles(null); }} style={{ fontSize:11, color:T.muted, background:"none", border:"none", cursor:"pointer", fontFamily:SANS, textDecoration:"underline" }}>Dismiss</button>
                  </div>
                  {qaBulkFiles.map(function(m, i) {
                    return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", fontSize:12 }}>
                        <span style={{ color:T.ink, flex:1 }}>{m.file}</span>
                        <span style={{ color:T.mutedLight }}>{"\u2192"}</span>
                        <span style={{ fontWeight:600, color:m.score > 0 ? T.success : T.muted }}>{m.match && m.score > 0 ? m.match.area + ": Q" + m.match.id.replace("qa-","") : "No match"}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <span style={{ fontSize:16, color:T.accent }}>{"\u2191"}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:T.ink }}>Bulk upload from your dataroom</div>
                    <div style={{ fontSize:12, color:T.muted }}>Drop supporting documents and Dilli will match them to questions</div>
                  </div>
                </div>
              )}
            </div>

            {/* Filter bar */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {["all", "pending", "needs-detail", "approved"].map(function(f) {
                var on = qaFilter === f;
                var label = f === "all" ? "All" : f === "needs-detail" ? "Needs detail" : f.charAt(0).toUpperCase() + f.slice(1);
                return <button key={f} onClick={function() { setQaFilter(f); }} style={{ padding: "5px 14px", fontSize: 11, fontWeight: on ? 600 : 400, borderRadius: 16, background: on ? T.ink : T.white, color: on ? T.white : T.muted, border: "1px solid " + (on ? T.ink : T.border), cursor: "pointer", fontFamily: SANS }}>{label}</button>;
              })}
            </div>

            {/* Questions grouped by area */}
            {Object.keys(qaByArea).map(function(area) {
              return (
                <div key={area} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.muted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>{area}</div>
                  {qaByArea[area].map(function(q) {
                    var isSelected = selectedQa && selectedQa.id === q.id;
                    return (
                      <div key={q.id} onClick={function() { setSelectedQa(q); setQaAnswer(q.answer || ""); }}
                        style={{ padding: "12px 16px", marginBottom: 6, borderRadius: 8, background: isSelected ? T.accentLight : T.white, border: "1px solid " + (isSelected ? T.accent : T.border), cursor: "pointer", transition: "all .12s" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <div style={{ flex: 1, fontSize: 13, color: T.ink, lineHeight: 1.5 }}>{q.question.length > 100 ? q.question.slice(0, 100) + "…" : q.question}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: qaStatusDot[q.status] }} />
                            <span style={{ fontSize: 10, color: T.muted }}>{qaStatusLabel[q.status]}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Right: detail panel */}
          {selectedQa ? (
            <div style={{ width: 480, borderLeft: "1px solid " + T.border, background: T.white, overflowY: "auto", padding: "28px 28px 28px 28px", animation: "slidePanel .25s ease both" }}>

              {/* Area badge */}
              <div style={{ fontSize: 10, fontWeight: 600, color: T.accent, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12 }}>{selectedQa.area}</div>

              {/* Question */}
              <div style={{ fontSize: 14, color: T.ink, lineHeight: 1.65, marginBottom: 24 }}>{selectedQa.question}</div>

              {/* Status */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: qaStatusDot[selectedQa.status] }} />
                <span style={{ fontSize: 12, fontWeight: 500, color: T.ink }}>{qaStatusLabel[selectedQa.status]}</span>
              </div>

              {/* Answer textarea */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Your Answer</div>
                <textarea
                  value={qaAnswer}
                  onChange={function(e) { setQaAnswer(e.target.value); }}
                  rows={6}
                  disabled={selectedQa.status === "approved"}
                  placeholder="Type your answer here…"
                  style={{ width: "100%", padding: 14, borderRadius: 8, border: "1px solid " + T.border, fontSize: 13, fontFamily: SANS, color: T.ink, background: selectedQa.status === "approved" ? T.bg : T.white, resize: "vertical", lineHeight: 1.6, boxSizing: "border-box" }}
                />
              </div>

              {/* Action buttons */}
              {selectedQa.status !== "approved" && (
                <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center" }}>
                  {/* AI check — secondary, always available */}
                  <button onClick={handleQaSubmit} disabled={qaReviewing || !qaAnswer.trim()}
                    style={{ padding: "10px 24px", borderRadius: 8, background: "none", color: qaReviewing || !qaAnswer.trim() ? T.mutedLight : T.muted, fontSize: 13, fontWeight: 500, border: "1px solid " + (qaReviewing || !qaAnswer.trim() ? T.border : T.border), cursor: qaReviewing || !qaAnswer.trim() ? "default" : "pointer", fontFamily: SANS, transition: "all .15s", display: "flex", alignItems: "center", gap: 8 }}>
                    {qaReviewing ? (
                      <>
                        <span style={{ width: 14, height: 14, border: "2px solid " + T.muted + "40", borderTopColor: T.muted, borderRadius: "50%", animation: "spin .6s linear infinite", display: "inline-block" }} />
                        Checking…
                      </>
                    ) : "Check with AI"}
                  </button>
                  {/* Submit — primary, always available */}
                  <button onClick={function() {
                    if (!qaAnswer.trim()) return;
                    var currentId = selectedQa.id;
                    setQaItems(function(items) {
                      return items.map(function(q) {
                        if (q.id !== currentId) return q;
                        return Object.assign({}, q, { answer: qaAnswer, status: "approved", aiReview: q.aiReview || null });
                      });
                    });
                    setSelectedQa(function(prev) {
                      if (!prev || prev.id !== currentId) return prev;
                      return Object.assign({}, prev, { answer: qaAnswer, status: "approved", aiReview: prev.aiReview || null });
                    });
                  }} disabled={!qaAnswer.trim()}
                    style={{ padding: "10px 24px", borderRadius: 8, background: !qaAnswer.trim() ? T.mutedLight : T.ink, color: T.white, fontSize: 13, fontWeight: 500, border: "none", cursor: !qaAnswer.trim() ? "default" : "pointer", fontFamily: SANS, transition: "background .15s", display: "flex", alignItems: "center", gap: 8 }}>
                    Submit answer
                  </button>
                </div>
              )}

              {/* AI Review result */}
              {selectedQa.aiReview && (
                <div style={{ padding: 16, borderRadius: 8, background: selectedQa.aiReview.status === "approved" ? "#F0FDF4" : "#FFFBEB", border: "1px solid " + (selectedQa.aiReview.status === "approved" ? "#BBF7D0" : "#FDE68A"), marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    {selectedQa.aiReview.status === "approved" ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.warn} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                    )}
                    <span style={{ fontSize: 12, fontWeight: 600, color: selectedQa.aiReview.status === "approved" ? T.success : T.warn }}>
                      {selectedQa.aiReview.status === "approved" ? "AI Review — Approved" : "AI Review — Needs more detail"}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.55 }}>{selectedQa.aiReview.feedback}</div>
                </div>
              )}

              {/* Reviewing state */}
              {qaReviewing && (
                <div style={{ padding: 16, borderRadius: 8, background: T.bg, border: "1px solid " + T.border, display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 16, height: 16, border: "2px solid " + T.accent + "40", borderTopColor: T.accent, borderRadius: "50%", animation: "spin .6s linear infinite", display: "inline-block" }} />
                  <span style={{ fontSize: 13, color: T.muted }}>AI is reviewing your answer…</span>
                </div>
              )}
            </div>
          ) : (
            <div style={{ width: 480, borderLeft: "1px solid " + T.border, background: T.white, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.2 }}>?</div>
                <div style={{ fontSize: 13, color: T.muted }}>Select a question to answer</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  var showOnboard = !onboardDismissed && onboardStep < ONBOARD_TIPS.length;

  /* ════ RFI VIEW ════ */
  return (
    <div style={{ fontFamily:SANS, minHeight:"100vh", background:T.bg, color:T.ink }}>
      <style dangerouslySetInnerHTML={{ __html:CSS }} />

      {/* ── Onboarding: dark backdrop (click-blocker) ── */}
      {showOnboard && tipRect && <div onClick={function() {
          if (onboardStep < ONBOARD_TIPS.length - 1) { setOnboardStep(function(s) { return s + 1; }); setTipRect(null); }
          else { setOnboardDismissed(true); }
        }} style={{ position:"fixed", inset:0, zIndex:9990, cursor:"pointer" }} />}

      {/* ── Onboarding: spotlight cutout ── */}
      {showOnboard && tipRect && <div style={{
        position:"fixed",
        top: tipRect.top - 8,
        left: tipRect.left - 8,
        width: tipRect.width + 16,
        height: tipRect.height + 16,
        borderRadius: 10,
        boxShadow: "0 0 0 9999px rgba(26,35,50,0.45)",
        transition: "all .35s ease",
        animation: "spotPulse 3s ease infinite",
        zIndex: 9991,
        pointerEvents: "none",
      }} />}

      {/* ── Onboarding: tooltip card ── */}
      {showOnboard && tipRect && (function() {
        var tipTop, tipLeft;
        if (tipRect.pos === "below") {
          tipTop = tipRect.top + tipRect.height + 18;
          tipLeft = Math.max(16, tipRect.left);
        } else if (tipRect.pos === "right") {
          tipTop = Math.max(80, tipRect.top);
          tipLeft = tipRect.left + tipRect.width + 18;
        } else if (tipRect.pos === "left") {
          tipTop = Math.max(80, tipRect.top);
          tipLeft = Math.max(16, tipRect.left - 278);
        } else {
          tipTop = tipRect.top + tipRect.height + 18;
          tipLeft = tipRect.left;
        }
        var tipText = ONBOARD_TIPS[onboardStep].text;
        return (
          <div key={"tip-" + onboardStep} style={{
            position: "fixed",
            top: tipTop,
            left: tipLeft,
            background: T.white,
            borderRadius: 14,
            padding: "18px 22px",
            width: 260,
            boxShadow: "0 8px 30px rgba(0,0,0,.18)",
            animation: "tipFadeIn .3s ease both",
            zIndex: 9999,
          }}>
            {/* Step dots */}
            <div style={{ display:"flex", gap:4, marginBottom:14 }}>
              {ONBOARD_TIPS.map(function(_, i) {
                return <div key={i} style={{ width:6, height:6, borderRadius:"50%", background: i <= onboardStep ? T.ink : T.border, transition:"background .3s" }} />;
              })}
            </div>
            <p style={{ fontSize: 14, color: T.ink, lineHeight: 1.6, margin: "0 0 16px", fontFamily: SANS }}>
              {tipText}
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <button
                onClick={function() { setOnboardDismissed(true); setSelected(null); }}
                style={{ fontSize: 12, color: T.muted, background: "none", border: "none", cursor: "pointer", fontFamily: SANS, textDecoration:"underline", textUnderlineOffset:3 }}>
                Skip
              </button>
              <button
                onClick={function() {
                  if (onboardStep < ONBOARD_TIPS.length - 1) {
                    setOnboardStep(function(s) { return s + 1; });
                    setTipRect(null);
                  } else {
                    setOnboardDismissed(true);
                  }
                }}
                style={{ padding: "8px 20px", borderRadius: 18, background: T.ink, color: T.white, fontSize: 12, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: SANS, transition: "background .15s" }}
                onMouseEnter={function(e) { e.currentTarget.style.background = T.inkSoft; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = T.ink; }}>
                {onboardStep < ONBOARD_TIPS.length - 1 ? "Next" : "Got it"}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Nav */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 32px", height:56, background:T.white, borderBottom:"1px solid " + T.border, position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="3" width="3" height="18" rx="1.5" fill={T.ink} />
            <rect x="7" y="6" width="3" height="12" rx="1.5" fill={T.accent} />
          </svg>
          <span style={{ fontSize:12, fontWeight:700, color:T.ink, letterSpacing:1.5, textTransform:"uppercase" }}>Dilli</span>
          <span style={{ color:T.border, margin:"0 4px" }}>/</span>
          <span style={{ fontSize:12, color:T.muted }}>Project Alpha</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <span style={{ fontSize:11, color:T.muted }}>{done}/{total} complete</span>
          <div style={{ width:80, height:2, background:T.border, borderRadius:1, overflow:"hidden" }}>
            <div style={{ height:"100%", width:(done / total * 100) + "%", background:T.accent, transition:"width .4s" }} />
          </div>
          {newCount > 0 && <span data-onboard="new-badge" style={{ fontSize:10, fontWeight:600, color:T.white, background:"#3B82F6", padding:"2px 8px", borderRadius:10 }}>{newCount} new</span>}
          {attn > 0 && <span style={{ fontSize:10, fontWeight:600, color:T.warn }}>{attn} need attention</span>}
          <div data-onboard="view-toggle" style={{ display:"flex", border:"1px solid " + T.border, borderRadius:5, overflow:"hidden" }}>
            {[["list","List"],["kanban","Board"]].map(function(v) {
              var on = boardView === v[0];
              return <button key={v[0]} onClick={function() { setBoardView(v[0]); }} style={{ padding:"5px 14px", fontSize:11, fontWeight:on ? 600 : 400, background:on ? T.ink : T.white, color:on ? T.white : T.muted, border:"none", cursor:"pointer", fontFamily:SANS }}>{v[1]}</button>;
            })}
          </div>
          <div style={{ display:"flex", border:"1px solid " + T.border, borderRadius:5, overflow:"hidden", marginLeft:4 }}>
            {[["rfi","RFI"],["qa","Q&A"]].map(function(v) {
              var on = view === v[0];
              return <button key={v[0]} onClick={function() { setView(v[0]); setSelected(null); setSelectedQa(null); }} style={{ padding:"5px 14px", fontSize:11, fontWeight:on ? 600 : 400, background:on ? T.ink : T.white, color:on ? T.white : T.muted, border:"none", cursor:"pointer", fontFamily:SANS }}>{v[1]}</button>;
            })}
          </div>
          <button onClick={function() { setView("home"); setSelected(null); }} style={{ fontSize:11, color:T.muted, background:"none", border:"none", cursor:"pointer", fontFamily:SANS }}>Home</button>
          <button onClick={function() { navigate("/"); }} style={{ fontSize:10, padding:"4px 12px", borderRadius:4, border:"1px dashed #d97706", color:"#d97706", background:"none", cursor:"pointer", fontFamily:SANS, fontWeight:500 }}>{"\u21C4"} Advisor View</button>
          <div style={{ width:28, height:28, borderRadius:"50%", background:T.ink, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontSize:10, fontWeight:700, color:T.accentMid }}>M</span>
          </div>
        </div>
      </div>

      <div style={{ display:"flex", height:"calc(100vh - 56px)" }}>

        {/* ── Left: items ── */}
        <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>

          {/* Bulk upload */}
          {showBulk && (
            <div
              data-onboard="bulk-upload"
              onDragOver={function(e) { e.preventDefault(); setDragBulk(true); }}
              onDragLeave={function() { setDragBulk(false); }}
              onDrop={function(e) { e.preventDefault(); setDragBulk(false); if (e.dataTransfer.files.length) handleBulkUpload(e.dataTransfer.files); }}
              onClick={function() { if (!bulkScanning && !bulkMatches) bulkRef.current && bulkRef.current.click(); }}
              style={{ marginBottom:16, padding:"18px 24px", borderRadius:8, background:T.white, border:dragBulk ? "1.5px solid " + T.accent : "1.5px dashed " + T.border, cursor:bulkScanning ? "default" : "pointer", transition:"all .15s" }}>
              <input ref={bulkRef} type="file" multiple style={{ display:"none" }} onChange={function(e) { if (e.target.files.length) handleBulkUpload(e.target.files); }} />

              {bulkScanning ? (
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:14, height:14, border:"2px solid " + T.accent, borderTopColor:"transparent", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
                  <span style={{ fontSize:13, color:T.ink }}>Matching files to requests…</span>
                </div>
              ) : bulkMatches ? (
                <div onClick={function(e) { e.stopPropagation(); }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:T.ink }}>Matched {bulkMatches.filter(function(m) { return m.score > 0; }).length} of {bulkMatches.length} files</span>
                    <button onClick={function() { setBulkMatches(null); setShowBulk(false); }} style={{ fontSize:11, color:T.muted, background:"none", border:"none", cursor:"pointer", fontFamily:SANS, textDecoration:"underline" }}>Dismiss</button>
                  </div>
                  {bulkMatches.map(function(m, i) {
                    return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", fontSize:12 }}>
                        <span style={{ color:T.ink, flex:1 }}>{m.file}</span>
                        <span style={{ color:T.mutedLight }}>{"\u2192"}</span>
                        <span style={{ fontWeight:600, color:m.score > 0 ? T.success : T.muted }}>{m.match && m.score > 0 ? m.match.id + " " + m.match.section : "No match"}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <span style={{ fontSize:16, color:T.accent }}>{"\u2191"}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:T.ink }}>Bulk upload from your dataroom</div>
                    <div style={{ fontSize:12, color:T.muted }}>Drop files here and Dilli will match them to requests automatically</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── LIST ── */}
          {boardView === "list" && (
            <div style={{ background:T.white, borderRadius:8, border:"1px solid " + T.border, overflow:"hidden" }}>
              {SECTIONS.map(function(sec, si) {
                var items = ITEMS.filter(function(i) { return i.id.startsWith(sec.key + "."); });
                return (
                  <div key={sec.key}>
                    <div style={{ padding:"8px 20px", background:T.bg, borderTop:si > 0 ? "1px solid " + T.border : "none", borderBottom:"1px solid " + T.border }}>
                      <span style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:1.5, textTransform:"uppercase" }}>{sec.label}</span>
                    </div>
                    {items.map(function(item, idx) {
                      var st = getStatus(item.id);
                      var isSel = selected && selected.id === item.id;
                      return (
                        <div key={item.id} onClick={function() { openItem(item); }}
                          style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 20px", borderBottom:idx < items.length - 1 ? "1px solid " + T.border + "66" : "none", cursor:"pointer", background:isSel ? T.accentLight + "66" : T.white, borderLeft:"3px solid " + (isSel ? T.accent : "transparent"), transition:"all .1s" }}
                          onMouseEnter={function(e) { if (!isSel) e.currentTarget.style.background = T.bg; }}
                          onMouseLeave={function(e) { e.currentTarget.style.background = isSel ? T.accentLight + "66" : T.white; }}>
                          {/* Priority dot */}
                          <div style={{ width:7, height:7, borderRadius:"50%", background:PRI_DOT[item.priority], flexShrink:0 }} />
                          {/* Content */}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <span style={{ fontSize:13, fontWeight:600, color:st === "complete" ? T.muted : T.ink, textDecoration:st === "complete" ? "line-through" : "none" }}>{item.section}</span>
                              {item.isNew && <span style={{ fontSize:9, fontWeight:700, color:"#3B82F6", background:"#EFF6FF", padding:"1px 6px", borderRadius:4, letterSpacing:.5, textTransform:"uppercase", flexShrink:0 }}>New</span>}
                            </div>
                            <div style={{ fontSize:11, color:T.muted, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.request}</div>
                          </div>
                          {/* File */}
                          {files[item.id] && <span style={{ fontSize:11, color:T.accent, flexShrink:0 }}>{"\u25C9"}</span>}
                          {/* Status */}
                          <div style={{ width:6, height:6, borderRadius:"50%", background:statusDot[st], flexShrink:0 }} />
                          <span style={{ fontSize:10, color:statusDot[st], fontWeight:500, flexShrink:0, width:80 }}>{statusLabel[st]}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── KANBAN ── */}
          {boardView === "kanban" && (function() {
            var PRI_ORDER = { Critical:1, High:2, Medium:3, Low:4 };
            var taggedNewCard = false;
            return (
            <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
              {[["open","To do"],["reviewing","Under review"],["attention","Needs attention"],["complete","Complete"]].map(function(col) {
                var colItems = ITEMS.filter(function(item) { return getStatus(item.id) === col[0] || (col[0] === "reviewing" && (getStatus(item.id) === "reviewing" || getStatus(item.id) === "uploaded")); });
                if (col[0] === "reviewing") colItems = ITEMS.filter(function(item) { var s = getStatus(item.id); return s === "reviewing" || s === "uploaded"; });
                colItems = colItems.slice().sort(function(a, b) { return (PRI_ORDER[a.priority] || 9) - (PRI_ORDER[b.priority] || 9); });
                return (
                  <div key={col[0]} style={{ flex:1, minWidth:180 }}>
                    <div data-onboard={col[0] === "open" ? "kanban-todo" : undefined} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                      <div style={{ width:6, height:6, borderRadius:"50%", background:statusDot[col[0]] || T.muted }} />
                      <span style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:1.2, textTransform:"uppercase" }}>{col[1]}</span>
                      <span style={{ fontSize:10, color:T.mutedLight }}>{colItems.length}</span>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6, minHeight:60 }}>
                      {colItems.map(function(item) {
                        var isFirstNew = item.isNew && !taggedNewCard;
                        if (isFirstNew) taggedNewCard = true;
                        var isSel = selected && selected.id === item.id;
                        return (
                          <div key={item.id} data-onboard={isFirstNew ? "new-card" : undefined} onClick={function() { openItem(item); }}
                            style={{ background:T.white, borderRadius:6, padding:"12px 14px", border:"1px solid " + (isSel ? T.accent : T.border), cursor:"pointer", opacity:col[0] === "complete" ? .5 : 1 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                              <div style={{ width:6, height:6, borderRadius:"50%", background:PRI_DOT[item.priority] }} />
                              <span style={{ fontSize:10, color:T.muted, flex:1 }}>{item.id}</span>
                              {item.isNew && <span style={{ fontSize:8, fontWeight:700, color:"#3B82F6", background:"#EFF6FF", padding:"1px 5px", borderRadius:3, letterSpacing:.5, textTransform:"uppercase" }}>New</span>}
                            </div>
                            <div style={{ fontSize:12, fontWeight:600, color:T.ink, marginBottom:3 }}>{item.section}</div>
                            <div style={{ fontSize:11, color:T.muted, lineHeight:1.4, overflow:"hidden", maxHeight:32 }}>{item.request}</div>
                          </div>
                        );
                      })}
                      {colItems.length === 0 && <div style={{ borderRadius:6, padding:"20px 12px", textAlign:"center", fontSize:11, color:T.mutedLight, border:"1px dashed " + T.border }}>None</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()}
        </div>

        {/* ── Right: Detail panel ── */}
        {selected && (
          <div style={{ width:440, background:T.white, borderLeft:"1px solid " + T.border, display:"flex", flexDirection:"column", animation:"slidePanel .2s ease both", flexShrink:0 }}>

            {/* Header — compact top bar */}
            <div style={{ padding:"14px 24px", borderBottom:"1px solid " + T.border, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:7, height:7, borderRadius:"50%", background:PRI_DOT[selected.priority] }} />
                <span style={{ fontFamily:SERIF, fontSize:20, fontWeight:400, color:T.ink, lineHeight:1.2 }}>{selected.section}</span>
                <span style={{ fontSize:10, color:T.muted }}>{selected.id}</span>
                {selected.isNew && <span style={{ fontSize:9, fontWeight:700, color:"#3B82F6", background:"#EFF6FF", padding:"2px 7px", borderRadius:4, letterSpacing:.5, textTransform:"uppercase" }}>New</span>}
              </div>
              <button onClick={function() { setSelected(null); }} style={{ background:"none", border:"none", fontSize:18, color:T.muted, cursor:"pointer", padding:4, lineHeight:1 }}>&times;</button>
            </div>

            {/* Scrollable body */}
            <div style={{ flex:1, overflowY:"auto", padding:0, display:"flex", flexDirection:"column" }}>

              {/* ── SECTION 1: The Request ── */}
              <div style={{ padding:"20px 24px", borderBottom:"1px solid " + T.border }}>
                <div style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:10 }}>Request</div>
                <p style={{ fontSize:13, color:T.ink, lineHeight:1.7, margin:0 }}>{selected.request}</p>
              </div>

              {/* ── SECTION 2: Your response (upload) ── */}
              <div style={{ padding:"20px 24px", borderBottom:"1px solid " + T.border }}>
                <div style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:10 }}>Your response</div>

                {!files[selected.id] && !scanning[selected.id] && (
                  <div>
                    <label data-onboard="upload-area" style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px", borderRadius:8, border:"1.5px dashed " + T.border, cursor:"pointer", transition:"all .12s" }}
                      onMouseEnter={function(e) { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.background = T.bg; }}
                      onMouseLeave={function(e) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "transparent"; }}>
                      <div style={{ width:32, height:32, borderRadius:8, background:T.accentLight, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        <span style={{ fontSize:15, color:T.accent }}>{"\u2191"}</span>
                      </div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:T.ink }}>Upload file</div>
                        <div style={{ fontSize:11, color:T.muted }}>Excel, PDF, or CSV</div>
                      </div>
                      <input type="file" style={{ display:"none" }} onChange={function(e) { if (e.target.files[0]) uploadFile(selected.id, e.target.files[0].name); }} />
                    </label>
                  </div>
                )}

                {scanning[selected.id] && (
                  <div style={{ borderRadius:10, border:"1.5px solid " + T.accentLight, background:T.bg, padding:"18px 20px", textAlign:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:10 }}>
                      <div style={{ width:16, height:16, border:"2px solid " + T.accent, borderTopColor:"transparent", borderRadius:"50%", animation:"spin .7s linear infinite", flexShrink:0 }} />
                      <span style={{ fontSize:13, fontWeight:600, color:T.ink }}>Verifying your information</span>
                    </div>
                    <div style={{ fontSize:12, color:T.muted, lineHeight:1.5 }}>Dilli is reviewing <span style={{ fontWeight:500, color:T.inkSoft }}>{files[selected.id]}</span> and checking it against the request. Results will appear below.</div>
                  </div>
                )}

                {files[selected.id] && !scanning[selected.id] && (function() {
                  var hasResult = scanned[selected.id];
                  var scanOk = hasResult && selected.scan && selected.scan.ok;
                  var scanBad = hasResult && selected.scan && !selected.scan.ok;
                  var noScanData = hasResult && !selected.scan;
                  var isGreen = scanOk || noScanData;
                  var isAmber = scanBad;
                  return (
                    <div style={{
                      padding:"12px 16px", borderRadius:10,
                      background:isGreen ? "#ECFDF5" : isAmber ? "#FFFBEB" : T.bg,
                      border:isGreen ? "1px solid #D1FAE5" : isAmber ? "1px solid #FEF3C7" : "none",
                    }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{
                          width:28, height:28, borderRadius:6, flexShrink:0,
                          background:isGreen ? T.success : isAmber ? T.warn : T.accentLight,
                          display:"flex", alignItems:"center", justifyContent:"center",
                        }}>
                          {isGreen
                            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            : isAmber
                              ? <span style={{ color:"#fff", fontSize:15, fontWeight:700, lineHeight:1 }}>!</span>
                              : <span style={{ fontSize:11, color:T.accent }}>{"\u25C9"}</span>
                          }
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, color:T.ink, wordBreak:"break-word" }}>{files[selected.id]}</div>
                          {isGreen && <div style={{ fontSize:11, fontWeight:600, color:T.success, marginTop:2 }}>Looks good</div>}
                          {isAmber && <div style={{ fontSize:11, fontWeight:600, color:T.warn, marginTop:2 }}>Requires attention</div>}
                        </div>
                        <label style={{ fontSize:11, color:isGreen ? T.success : isAmber ? T.warn : T.accent, cursor:"pointer", fontWeight:500, flexShrink:0 }}>
                          Replace
                          <input type="file" style={{ display:"none" }} onChange={function(e) { if (e.target.files[0]) uploadFile(selected.id, e.target.files[0].name); }} />
                        </label>
                      </div>
                      {/* Green: summary */}
                      {isGreen && selected.scan && selected.scan.summary && (
                        <div style={{ fontSize:12, color:T.inkSoft, lineHeight:1.5, marginTop:8, paddingTop:8, borderTop:"1px solid #D1FAE5" }}>
                          {selected.scan.summary}
                        </div>
                      )}
                      {/* Amber: missing items list */}
                      {isAmber && selected.scan.missing && (
                        <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid #FEF3C7" }}>
                          <div style={{ fontSize:10, fontWeight:700, color:T.warn, letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>Missing</div>
                          {selected.scan.missing.map(function(item, j) {
                            return <div key={j} style={{ display:"flex", gap:8, marginBottom:j < selected.scan.missing.length - 1 ? 4 : 0, fontSize:12, color:T.inkSoft, lineHeight:1.5 }}>
                              <span style={{ color:T.warn, flexShrink:0 }}>{"\u2022"}</span>
                              <span>{item}</span>
                            </div>;
                          })}
                        </div>
                      )}
                      {/* Amber: action needed */}
                      {isAmber && selected.scan && selected.scan.fix && (
                        <div style={{ fontSize:12, color:T.inkSoft, lineHeight:1.5, marginTop:8, paddingTop:8, borderTop:"1px solid #FEF3C7" }}>
                          <span style={{ fontWeight:600, color:T.warn }}>Action needed: </span>{selected.scan.fix}
                        </div>
                      )}
                      {/* Clarification / Our questions */}
                      {hasResult && selected.scan && selected.scan.clarification && (
                        <div style={{ fontSize:12, color:"#4338CA", lineHeight:1.5, marginTop:8, paddingTop:8, borderTop:isGreen ? "1px solid #D1FAE5" : "1px solid #FEF3C7" }}>
                          <span style={{ fontWeight:600 }}>Our question: </span>{selected.scan.clarification}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Notes */}
                <textarea
                  data-onboard="notes-area"
                  value={notes[selected.id] || ""}
                  onChange={function(e) { setNotes(function(n) { return Object.assign({}, n, { [selected.id]: e.target.value }); }); }}
                  placeholder="Add a note for your advisor..."
                  style={{ width:"100%", marginTop:12, fontSize:12, border:"1px solid " + T.border, borderRadius:8, padding:"10px 14px", resize:"none", minHeight:48, color:T.ink, lineHeight:1.6, background:T.white, fontFamily:SANS, boxSizing:"border-box", transition:"border-color .12s" }}
                  onFocus={function(e) { e.target.style.borderColor = T.accent; }}
                  onBlur={function(e) { e.target.style.borderColor = T.border; }}
                />
                {/* Submit button — always visible when there's content */}
                {((notes[selected.id] || "").trim() || files[selected.id]) && (
                  <button
                    onClick={function() {
                      var id = selected.id;
                      setScanned(function(s) { return Object.assign({}, s, { [id]: true }); });
                      if (!files[id]) {
                        setFiles(function(f) { return Object.assign({}, f, { [id]: "Submitted" }); });
                      }
                    }}
                    style={{ marginTop:8, padding:"10px 24px", borderRadius:20, background:T.ink, color:T.white, fontSize:13, fontWeight:500, border:"none", cursor:"pointer", fontFamily:SANS, display:"flex", alignItems:"center", gap:8, transition:"background .15s" }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = T.inkSoft; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = T.ink; }}>
                    Submit
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </button>
                )}

                {/* Template download — always visible */}
                <button data-onboard="template-link" onClick={function() { dlTemplate(selected); }} style={{ marginTop:8, fontSize:11, color:T.accent, background:"none", border:"none", cursor:"pointer", fontFamily:SANS, textDecoration:"underline", textUnderlineOffset:2 }}>Download template</button>

                {/* ── Need guidance — chat with Dilli ── */}
                {!chatOpen && (
                  <div style={{ marginTop:12, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div style={{ fontSize:12, color:T.muted }}>Need guidance? <span style={{ color:T.accent, fontWeight:500 }}>Chat with Dilli</span></div>
                    <button onClick={function() { setChatOpen(true); }}
                      style={{ width:36, height:36, borderRadius:"50%", background:"linear-gradient(135deg, #5B9EA1, #7AB8A8)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(91,158,161,.3)", transition:"transform .15s, box-shadow .15s", flexShrink:0 }}
                      onMouseEnter={function(e) { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(91,158,161,.4)"; }}
                      onMouseLeave={function(e) { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(91,158,161,.3)"; }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </button>
                  </div>
                )}

              </div>

              {/* ── Chat with Dilli (expanded) ── */}
              {chatOpen && (
                <div style={{ padding:"20px 24px", flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <DilliDot />
                      <span style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:1.5, textTransform:"uppercase" }}>Chat with Dilli</span>
                    </div>
                    <button onClick={function() { setChatOpen(false); }} style={{ background:"none", border:"none", fontSize:11, color:T.muted, cursor:"pointer", fontFamily:SANS, padding:0 }}>Close</button>
                  </div>

                  {/* Chat messages */}
                  {dillyMsgs.map(function(msg, i) {
                    if (msg.from === "user") {
                      return (
                        <div key={i} style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
                          <div style={{ maxWidth:"82%", padding:"10px 14px", borderRadius:"12px 12px 2px 12px", background:T.ink, color:T.white, fontSize:13, lineHeight:1.6 }}>{msg.text}</div>
                        </div>
                      );
                    }
                    return (
                      <div key={i} style={{ marginBottom:8, padding:"12px 16px", borderRadius:10, background:T.bg, fontSize:13, lineHeight:1.65, color:T.ink }}>{msg.text}</div>
                    );
                  })}

                  {/* Example questions — show when no user message sent yet */}
                  {!dillyMsgs.some(function(m) { return m.from === "user"; }) && (
                    <div style={{ marginTop:0 }}>
                      <div style={{ fontSize:10, fontWeight:600, color:T.muted, letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>Try asking</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {[
                          "What is LTM?",
                          "What format do you need?",
                          "Show me the template",
                          "Can I upload multiple files?",
                          "What does this request mean?"
                        ].map(function(q) {
                          return (
                            <button key={q} onClick={function() { setDilliInput(q); }}
                              style={{ padding:"6px 12px", borderRadius:16, background:T.bg, border:"1px solid " + T.border, fontSize:12, color:T.inkSoft, cursor:"pointer", fontFamily:SANS, transition:"all .12s", lineHeight:1.3 }}
                              onMouseEnter={function(e) { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.background = T.accentLight; }}
                              onMouseLeave={function(e) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.bg; }}>
                              {q}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div ref={chatEnd} />
                </div>
              )}
            </div>

            {/* Footer — Ask Dilli input (only when chat is open) */}
            {chatOpen && (
              <div style={{ padding:"14px 24px 18px", borderTop:"1px solid " + T.border, flexShrink:0, background:T.white }}>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <DilliDot />
                  <div style={{ flex:1, position:"relative" }}>
                    <input value={dillyInput}
                      onChange={function(e) { setDilliInput(e.target.value); }}
                      onKeyDown={function(e) { if (e.key === "Enter") sendDilli(dillyInput); }}
                      placeholder="Ask Dilli a question…"
                      style={{ width:"100%", padding:"10px 42px 10px 14px", borderRadius:20, border:"1px solid " + T.border, fontSize:13, background:T.bg, color:T.ink, fontFamily:SANS, transition:"border-color .12s", boxSizing:"border-box" }}
                      onFocus={function(e) { e.target.style.borderColor = T.accent; }}
                      onBlur={function(e) { e.target.style.borderColor = T.border; }}
                    />
                    <button onClick={function() { sendDilli(dillyInput); }}
                      style={{ position:"absolute", right:4, top:"50%", transform:"translateY(-50%)", width:30, height:30, borderRadius:"50%", background:dillyInput.trim() ? T.accent : T.mutedLight, color:T.white, border:"none", cursor:dillyInput.trim() ? "pointer" : "default", display:"flex", alignItems:"center", justifyContent:"center", transition:"background .15s", flexShrink:0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
